import React, { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";

const LOGISTICS_HEADERS = [
  "收件人姓名",
  "聯絡電話",
  "公司",
  "郵遞區號",
  "地址",
  "貨物類型",
  "貨物名稱",
  "貨物件數",
  "貨物重量(kg)",
  "貨物長度(m)",
  "貨物寬度(m)",
  "貨物高度(m)",
  "材積(cbm)",
  "棧板數",
  "溫度類型",
];

const LOCATED_HEADERS = [
  ...LOGISTICS_HEADERS,
  "定位原始地址",
  "定位後地址",
  "定位郵遞區號",
  "緯度",
  "經度",
];

const COMPANY_API_URL = "https://asia-east1-chat3test-59045.cloudfunctions.net/createCompanyAccount";

const TEMPLATE_CSV = [
  LOGISTICS_HEADERS.join(","),
  "王小明,0912345678,高雄食品有限公司,807,高雄市三民區建國三路123號4樓,GENERAL,冷藏食品,8,320,,,2.4,,冷藏",
  "李四,0225001234,台北物流有限公司,104,台北市中山區南京東路二段99號,GENERAL,一般貨物,12,180,,,1.2,,",
].join("\n");

const todayInput = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const text = (value) => String(value ?? "").trim();

function coordsOK(lat, lng) {
  const latitude = Number(lat);
  const longitude = Number(lng);
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180
    && !(Math.abs(latitude) < 0.000001 && Math.abs(longitude) < 0.000001);
}

function addressKey(value) {
  return text(value).replace(/[\s,，。．]/g, "").toLowerCase();
}

async function requestHereGeocode(address, orgId, signal) {
  const currentUser = auth.currentUser;
  if (!currentUser?.uid) throw new Error("尚未登入 Firebase，無法取得地址定位。");
  if (!orgId) throw new Error("目前登入帳號沒有 orgId，無法取得地址定位。");
  const idToken = await currentUser.getIdToken();
  const response = await fetch(COMPANY_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ action: "geocodeAddress", orgId, address: text(address) }),
    signal,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.ok) throw new Error(result.message || "地址查詢失敗。");
  return result.results?.[0] || null;
}

function numberOrNull(value) {
  const raw = text(value).replace(/,/g, "");
  if (!raw) return null;
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
}

function normalizePhone(value, autoAddMobileLeadingZero = true) {
  let phone = text(value).replace(/[\s()\-]/g, "");
  if (!phone) return "";
  if (autoAddMobileLeadingZero && phone.startsWith("+886") && /^\+8869\d{8}$/.test(phone)) {
    return `0${phone.slice(4)}`;
  }
  if (autoAddMobileLeadingZero && phone.startsWith("886") && /^8869\d{8}$/.test(phone)) {
    return `0${phone.slice(3)}`;
  }
  if (autoAddMobileLeadingZero && /^9\d{8}$/.test(phone)) return `0${phone}`;
  return phone;
}

function normalizeTemperature(value) {
  const token = text(value).toUpperCase().replace(/[\s_\-\/／]/g, "");
  if (!token || ["NONE", "無", "無指定", "不拘", "無溫度需求"].includes(token)) return "NONE";
  if (["AMBIENT", "常溫", "常温"].includes(token)) return "AMBIENT";
  if (["CHILLED", "冷藏"].includes(token)) return "CHILLED";
  if (["FROZEN", "冷凍", "冷冻"].includes(token)) return "FROZEN";
  return "";
}

function temperatureLabel(value) {
  return {
    NONE: "無溫度需求",
    AMBIENT: "常溫",
    CHILLED: "冷藏",
    FROZEN: "冷凍",
  }[text(value).toUpperCase()] || "未指定";
}

function normalizeCargoType(value) {
  const token = text(value).toUpperCase().replace(/[\s_\-\/／]/g, "");
  if (["GENERAL", "一般", "一般貨物", "雜貨", "箱件"].includes(token)) return "GENERAL";
  if (["STEELPLATE", "鋼板"].includes(token)) return "STEEL_PLATE";
  if (["STEELCOIL", "鋼捲", "鋼卷"].includes(token)) return "STEEL_COIL";
  if (["VEHICLE", "汽車", "車輛", "汽車車輛", "轎車", "車輛運輸"].includes(token)) return "VEHICLE";
  if (["EXCAVATOR", "挖土機", "挖掘機"].includes(token)) return "EXCAVATOR";
  if (["HEAVYMACHINERY", "重型機械", "工程機械", "機械設備"].includes(token)) return "HEAVY_MACHINERY";
  if (["BUILDINGMATERIAL", "建材"].includes(token)) return "BUILDING_MATERIAL";
  if (["AGRICULTURAL", "農產品", "農產"].includes(token)) return "AGRICULTURAL";
  if (["FOOD", "食品"].includes(token)) return "FOOD";
  if (["OTHER", "其他"].includes(token)) return "OTHER";
  return "";
}

function cargoTypeLabel(value) {
  return {
    GENERAL: "一般貨物",
    STEEL_PLATE: "鋼板",
    STEEL_COIL: "鋼捲",
    VEHICLE: "汽車／車輛",
    EXCAVATOR: "挖土機",
    HEAVY_MACHINERY: "重型機械",
    BUILDING_MATERIAL: "建材",
    AGRICULTURAL: "農產品",
    FOOD: "食品",
    OTHER: "其他",
  }[text(value).toUpperCase()] || "未指定";
}

function dimensionLabel(row) {
  const values = [row.lengthM, row.widthM, row.heightM];
  return values.every((value) => value !== null && value !== undefined)
    ? `${values.join(" × ")} m`
    : "未填尺寸";
}

function pick(row, aliases) {
  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(row, alias) && text(row[alias])) return text(row[alias]);
  }
  return "";
}

function parseCsv(content) {
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < content.length; index += 1) {
    const character = content[index];
    const next = content[index + 1];
    if (character === '"' && quoted && next === '"') {
      cell += '"';
      index += 1;
      continue;
    }
    if (character === '"') {
      quoted = !quoted;
      continue;
    }
    if (character === "," && !quoted) {
      row.push(cell.trim());
      cell = "";
      continue;
    }
    if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && next === "\n") index += 1;
      row.push(cell.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += character;
  }
  row.push(cell.trim());
  if (row.some(Boolean)) rows.push(row);
  const headers = (rows.shift() || []).map((header) => header.replace(/^\uFEFF/, "").trim());
  return rows.map((values) => Object.fromEntries(headers.map((header, index) => [header, values[index] || ""])));
}

function mapCsvRow(raw, rowNumber, autoAddMobileLeadingZero = true) {
  const recipientName = pick(raw, ["收件人姓名", "收件人", "姓名", "recipientName"]);
  const phoneInput = pick(raw, ["聯絡電話", "電話", "手機", "phone"]);
  const phone = normalizePhone(phoneInput, autoAddMobileLeadingZero);
  const company = pick(raw, ["公司", "公司名稱", "company"]);
  const postalCode = pick(raw, ["郵遞區號", "郵遞區號(選填)", "postalCode"]);
  const address = pick(raw, ["地址", "地址文字", "配送地址", "address"]);
  const cargoType = normalizeCargoType(pick(raw, ["貨物類型", "貨品類型", "cargoType"]));
  const cargoName = pick(raw, ["貨物名稱", "貨品名稱", "品名", "cargoName"]);
  const cargoCount = numberOrNull(pick(raw, ["貨物件數", "件數", "數量", "cargoCount", "quantity"])) ?? 1;
  const weightKg = numberOrNull(pick(raw, ["貨物重量(kg)", "貨物重量", "重量(kg)", "重量", "weightKg"]));
  const lengthM = numberOrNull(pick(raw, ["貨物長度(m)", "長度(m)", "長度", "lengthM"]));
  const widthM = numberOrNull(pick(raw, ["貨物寬度(m)", "寬度(m)", "寬度", "widthM"]));
  const heightM = numberOrNull(pick(raw, ["貨物高度(m)", "高度(m)", "高度", "heightM"]));
  const volumeCbm = numberOrNull(pick(raw, ["材積(cbm)", "材積", "體積(cbm)", "volumeCbm"]));
  const palletCount = numberOrNull(pick(raw, ["棧板數", "棧板", "palletCount"]));
  const temperatureType = normalizeTemperature(pick(raw, ["溫度類型", "溫層", "temperatureType"]));
  const addressOriginal = pick(raw, ["定位原始地址", "addressOriginal"]) || address;
  const addressResolved = pick(raw, ["定位後地址", "addressResolved"]);
  const resolvedPostalCode = pick(raw, ["定位郵遞區號", "resolvedPostalCode"]) || postalCode;
  const lat = numberOrNull(pick(raw, ["緯度", "lat"]));
  const lng = numberOrNull(pick(raw, ["經度", "lng"]));
  const savedAddressMatches = !addressOriginal || addressKey(addressOriginal) === addressKey(address);
  const hasSavedLocation = coordsOK(lat, lng) && savedAddressMatches;
  const errors = [];
  const warnings = [];
  const normalizedPhone = phone;

  if (!recipientName) errors.push("缺少收件人姓名");
  if (!phone) errors.push("缺少聯絡電話");
  else if (!/^\+?[0-9]{8,15}$/.test(phone)) errors.push("聯絡電話格式不正確");
  else if (!autoAddMobileLeadingZero && /^9\d{8}$/.test(phone)) warnings.push("手機號碼少了開頭 0，目前未自動補上");
  if (!address) errors.push("缺少配送地址");
  if (!cargoType) errors.push("缺少或無法辨識貨物類型");
  if (!cargoName) errors.push("缺少貨物名稱");
  if (cargoCount === null || cargoCount <= 0) errors.push("貨物件數必須大於 0");
  if (weightKg === null || weightKg < 0) errors.push("缺少有效的貨物重量");
  if (temperatureType === "") errors.push("溫度類型無法辨識");
  if (volumeCbm === null && (lengthM === null || widthM === null || heightM === null)) warnings.push("未填材積或完整尺寸，後續無法進行空間容量檢查");
  else if (volumeCbm < 0) errors.push("材積不可小於 0");
  if ([lengthM, widthM, heightM].some((value) => value !== null && value < 0)) errors.push("貨物尺寸不可小於 0");
  if (palletCount !== null && palletCount < 0) errors.push("棧板數不可小於 0");
  if (coordsOK(lat, lng) && !savedAddressMatches) warnings.push("地址文字已變更，舊定位結果不再沿用");

  return {
    rowNumber,
    recipientName,
    phone,
    normalizedPhone,
    company,
    postalCode,
    address,
    cargoType,
    cargoName,
    cargoCount,
    weightKg,
    lengthM,
    widthM,
    heightM,
    volumeCbm,
    palletCount,
    temperatureType,
    addressOriginal,
    addressResolved,
    resolvedPostalCode,
    lat: hasSavedLocation ? lat : null,
    lng: hasSavedLocation ? lng : null,
    geocodeStatus: hasSavedLocation ? "located" : "pending",
    geocodeMessage: hasSavedLocation ? "已沿用 CSV 內的定位結果。" : "",
    errors,
    warnings,
    valid: errors.length === 0,
  };
}

function preflightRows(rawRows, autoAddMobileLeadingZero = true) {
  const rows = rawRows.map((row, index) => mapCsvRow(row, index + 2, autoAddMobileLeadingZero));
  const duplicateKeys = new Map();
  rows.forEach((row) => {
    const key = `${row.normalizedPhone}|${row.address}`.toLowerCase();
    if (key !== "|") duplicateKeys.set(key, [...(duplicateKeys.get(key) || []), row.rowNumber]);
  });
  rows.forEach((row) => {
    const key = `${row.normalizedPhone}|${row.address}`.toLowerCase();
    if ((duplicateKeys.get(key) || []).length > 1) row.warnings.push(`可能重複配送資料：第 ${duplicateKeys.get(key).join("、")} 列`);
  });
  return rows;
}

function downloadTextFile(filename, content) {
  const blob = new Blob(["\uFEFF", content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function csvEscape(value) {
  const raw = String(value ?? "");
  return /[",\r\n]/.test(raw) ? `"${raw.replace(/"/g, '""')}"` : raw;
}

function locatedCsvContent(rows) {
  return [
    LOCATED_HEADERS.join(","),
    ...rows.map((row) => [
      row.recipientName,
      row.phone,
      row.company,
      row.postalCode,
      row.address,
      cargoTypeLabel(row.cargoType),
      row.cargoName,
      row.cargoCount,
      row.weightKg,
      row.lengthM,
      row.widthM,
      row.heightM,
      row.volumeCbm,
      row.palletCount,
      temperatureLabel(row.temperatureType),
      row.addressOriginal,
      row.addressResolved,
      row.resolvedPostalCode,
      row.lat,
      row.lng,
    ].map(csvEscape).join(",")),
  ].join("\n");
}

function Icon({ name, size = 18 }) {
  const paths = {
    upload: "M12 16V4m0 0L7 9m5-5 5 5M5 20h14",
    download: "M12 4v12m0 0 5-5m-5 5-5-5M5 20h14",
    file: "M6 3h9l3 3v15H6zM14 3v4h4M9 12h6M9 16h6",
    check: "m5 12 4 4L19 6",
    alert: "M12 4 21 20H3L12 4Zm0 5v5m0 3h.01",
    search: "m16 16 5 5M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0",
    calendar: "M5 4h14v16H5zM8 2v4m8-4v4M5 9h14",
    truck: "M2 5h12v12H2zM14 9h4l4 4v4h-8M8 18a2 2 0 1 1-4 0M20 18a2 2 0 1 1-4 0",
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={paths[name] || paths.file} /></svg>;
}

export default function QuickCreateFleetLogisticsJob({ user: propUser, account: propAccount, initialData, onNext, onBack }) {
  const [authUser, setAuthUser] = useState(propUser || auth.currentUser || null);
  const [authAccount, setAuthAccount] = useState(propAccount || null);
  const [authChecked, setAuthChecked] = useState(false);
  const [jobDate, setJobDate] = useState(initialData?.jobDate || todayInput);
  const [fileName, setFileName] = useState(initialData?.fileName || "");
  const [rawRows, setRawRows] = useState(initialData?.rawRows || []);
  const [rows, setRows] = useState(initialData?.rows || []);
  const [autoAddMobileLeadingZero, setAutoAddMobileLeadingZero] = useState(initialData?.autoAddMobileLeadingZero ?? true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [phase, setPhase] = useState(() => {
    const initialRows = initialData?.rows || [];
    return initialRows.length && initialRows.every((row) => row.valid && row.geocodeStatus === "located") ? "located" : "preflight";
  });
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState({ done: 0, total: 0 });
  const [geocodeCache, setGeocodeCache] = useState({});
  const abortRef = useRef(null);

  const user = propUser || authUser;
  const account = propAccount || authAccount;
  const orgId = text(account?.orgId);

  useEffect(() => {
    let alive = true;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!alive) return;
      setAuthUser(firebaseUser || null);
      setAuthChecked(true);
      if (!firebaseUser?.uid) {
        setAuthAccount(null);
        return;
      }
      if (propAccount?.orgId) return;
      try {
        const snapshot = await getDoc(doc(db, "drivers", firebaseUser.uid));
        if (alive) setAuthAccount(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
      } catch (accountError) {
        console.error("[QuickCreateFleetLogisticsJob] account load:", accountError);
        if (alive) setAuthAccount(null);
      }
    });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [propAccount]);

  const validCount = useMemo(() => rows.filter((row) => row.valid).length, [rows]);
  const errorCount = rows.length - validCount;
  const locatedCount = useMemo(() => rows.filter((row) => row.geocodeStatus === "located").length, [rows]);
  const addressErrorCount = useMemo(() => rows.filter((row) => row.geocodeStatus === "not_found" || row.geocodeStatus === "error").length, [rows]);
  const unresolvedCount = rows.length - locatedCount;
  const filteredRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return rows;
    return rows.filter((row) => [row.rowNumber, row.recipientName, row.phone, row.address, row.addressResolved, cargoTypeLabel(row.cargoType), row.cargoName, temperatureLabel(row.temperatureType), row.geocodeStatus].join(" ").toLowerCase().includes(keyword));
  }, [rows, search]);

  function clearMessages() {
    setError("");
    setNotice("");
  }

  function locationStatusLabel(row) {
    if (row.geocodeStatus === "located") return "已定位";
    if (row.geocodeStatus === "searching") return "查詢中";
    if (row.geocodeStatus === "not_found") return "查無結果";
    if (row.geocodeStatus === "error") return "查詢失敗";
    return "待查詢";
  }

  function downloadLocated() {
    if (!rows.length) return setError("目前沒有可下載的資料。");
    if (errorCount) return setError(`還有 ${errorCount} 筆資料有欄位錯誤，請修正後再下載。`);
    if (unresolvedCount) return setError(`還有 ${unresolvedCount} 筆地址尚未定位，完成後才能下載已定位 CSV。`);
    const baseName = fileName.replace(/\.csv$/i, "") || "logistics";
    downloadTextFile(`${baseName}_已定位.csv`, locatedCsvContent(rows));
    setNotice("已下載定位完成的 CSV；下次匯入這份檔案時，系統會直接沿用緯度、經度與定位後地址。");
  }

  function continueToVehicleStep() {
    clearMessages();
    if (!rows.length) return setError("請先匯入 CSV。");
    if (errorCount) return setError(`還有 ${errorCount} 筆資料有欄位錯誤，請先修正。`);
    if (unresolvedCount) return setError(`還有 ${unresolvedCount} 筆地址尚未定位，請先完成地址比對。`);
    onNext?.({
      jobDate,
      fileName,
      orgId,
      rows,
      rawRows,
      autoAddMobileLeadingZero,
    });
  }

  async function startGeocoding() {
    clearMessages();
    if (!rows.length) return setError("請先匯入 CSV。");
    if (errorCount) return setError(`目前有 ${errorCount} 筆資料不能查地址，請先修正紅色錯誤。`);
    if (!orgId) return setError("目前登入帳號尚未取得 orgId，無法進行地址比對。");
    const pendingRows = rows.filter((row) => row.valid && row.geocodeStatus !== "located");
    if (!pendingRows.length) {
      setPhase("located");
      setNotice("這批資料的地址都已經有定位結果，現在可以下載已定位 CSV。");
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setGeocoding(true);
    setPhase("geocoding");
    setGeocodeProgress({ done: 0, total: pendingRows.length });
    let nextRows = [...rows];
    const currentCache = { ...geocodeCache };
    let done = 0;
    try {
      for (const row of pendingRows) {
        if (controller.signal.aborted) throw new DOMException("查詢已取消", "AbortError");
        const key = addressKey(row.address);
        let result = currentCache[key];
        const fromCache = Boolean(result);
        try {
          if (!result) {
            result = await requestHereGeocode(row.address, orgId, controller.signal);
            if (result) currentCache[key] = result;
          }
          const lat = Number(result?.lat);
          const lng = Number(result?.lng);
          if (!result || !coordsOK(lat, lng)) {
            throw new Error("地址查詢服務沒有回傳有效座標");
          }
          const resolvedAddress = text(result.address || result.label || row.address);
          nextRows = nextRows.map((item) => item.rowNumber === row.rowNumber ? {
            ...item,
            addressResolved: resolvedAddress,
            resolvedPostalCode: text(result.postalCode) || item.resolvedPostalCode || item.postalCode,
            lat,
            lng,
            geocodeStatus: "located",
            geocodeMessage: fromCache ? "沿用本次匯入快取" : "定位完成",
          } : item);
        } catch (rowError) {
          if (rowError?.name === "AbortError") throw rowError;
          nextRows = nextRows.map((item) => item.rowNumber === row.rowNumber ? {
            ...item,
            geocodeStatus: "not_found",
            geocodeMessage: rowError?.message || "找不到可用定位結果",
          } : item);
        }
        done += 1;
        setGeocodeProgress({ done, total: pendingRows.length });
        setRows([...nextRows]);
      }
      const remaining = nextRows.filter((row) => row.valid && row.geocodeStatus !== "located").length;
      setGeocodeCache(currentCache);
      setPhase(remaining ? "address_review" : "located");
      setNotice(remaining ? `地址比對完成，${remaining} 筆需要重新確認；已定位資料可以保留。` : `地址比對完成，共 ${nextRows.length} 筆都已定位。現在可以下載已定位 CSV。`);
    } catch (geocodeError) {
      if (geocodeError?.name !== "AbortError") {
        console.error("[QuickCreateFleetLogisticsJob] address geocode:", geocodeError);
        setError(geocodeError?.message || "地址比對中斷，請稍後重試未完成的地址。");
      } else {
        setNotice("地址比對已停止；已完成的定位結果已保留。");
      }
      setRows([...nextRows]);
      setPhase("address_review");
    } finally {
      abortRef.current = null;
      setGeocoding(false);
    }
  }

  function stopGeocoding() {
    abortRef.current?.abort();
  }

  function handlePhoneFormatChange(event) {
    const enabled = event.target.checked;
    setAutoAddMobileLeadingZero(enabled);
    if (!rawRows.length || geocoding) return;
    const previousByRow = new Map(rows.map((row) => [row.rowNumber, row]));
    const nextRows = preflightRows(rawRows, enabled).map((row) => {
      const previous = previousByRow.get(row.rowNumber);
      if (!previous || previous.geocodeStatus !== "located") return row;
      return {
        ...row,
        addressOriginal: previous.addressOriginal,
        addressResolved: previous.addressResolved,
        resolvedPostalCode: previous.resolvedPostalCode,
        lat: previous.lat,
        lng: previous.lng,
        geocodeStatus: previous.geocodeStatus,
        geocodeMessage: previous.geocodeMessage,
      };
    });
    setRows(nextRows);
    setPhase(nextRows.every((row) => row.valid) && nextRows.every((row) => row.geocodeStatus === "located") ? "located" : "preflight");
    setNotice(enabled ? "已開啟手機號碼自動補 0；只會處理 9 碼手機號碼。" : "已關閉手機號碼自動補 0；未含 0 的手機號碼會保留原樣並顯示提醒。");
  }

  async function handleFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    abortRef.current?.abort();
    clearMessages();
    setPhase("preflight");
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setError("目前只接受 CSV 檔案，請將 Excel 另存為 UTF-8 CSV 後再匯入。");
      return;
    }
    try {
      const content = await file.text();
      const rawRows = parseCsv(content);
      if (!rawRows.length) {
        setError("CSV 裡沒有可預檢的資料列。");
        return;
      }
      if (rawRows.length > 240) {
        setError("單次最多先匯入 240 筆，避免一次預檢資料過大。");
        return;
      }
      const nextRows = preflightRows(rawRows);
      setRawRows(rawRows);
      setRows(nextRows);
      setFileName(file.name);
      const savedLocationCount = nextRows.filter((row) => row.geocodeStatus === "located").length;
      const allRowsValid = nextRows.every((row) => row.valid);
      setPhase(allRowsValid && savedLocationCount === nextRows.length ? "located" : "preflight");
      setNotice(savedLocationCount
        ? `已完成 ${nextRows.length} 筆本機預檢，其中 ${savedLocationCount} 筆已有定位結果，將直接沿用。`
        : `已完成 ${nextRows.length} 筆本機預檢；尚未查地址，也尚未寫入 Firebase。`);
    } catch (parseError) {
      console.error("[QuickCreateFleetLogisticsJob] CSV parse:", parseError);
      setError("CSV 解析失敗，請確認檔案是 UTF-8 CSV，且第一列是欄位名稱。");
    }
  }

  if (!authChecked) return <div className="qcl-center"><span className="qcl-spinner" />驗證登入狀態中…<style>{styles}</style></div>;
  if (!user?.uid) return <div className="qcl-center"><strong>快速建立物流工作</strong><span>請先登入帳號。</span><style>{styles}</style></div>;

  return (
    <div className="qcl-page">
      <header className="qcl-header">
        <div><div className="qcl-eyebrow">QUICK CREATE</div><h1>快速建立物流工作</h1><p>先匯入配送資料並完成預檢，地址比對與車輛分配會在下一步處理。</p></div>
        <div className="qcl-actions">{typeof onBack === "function" ? <button type="button" className="qcl-secondary" onClick={onBack}>返回工作管理</button> : null}<button type="button" className="qcl-secondary" onClick={() => downloadTextFile("LogisticsJobImportTemplate.csv", TEMPLATE_CSV)}><Icon name="download" size={16} />下載 CSV 範本</button><label className="qcl-primary"><Icon name="upload" size={16} />匯入配送 CSV<input type="file" accept=".csv,text/csv" onChange={handleFile} /></label></div>
      </header>
      <section className="qcl-info"><span className="qcl-info-icon"><Icon name={phase === "located" ? "check" : phase === "geocoding" ? "search" : "file"} size={18} /></span><div><strong>{phase === "preflight" ? "第一步：資料預檢" : phase === "geocoding" ? "第二步：地址比對中" : phase === "located" ? "地址比對完成" : "第二步：確認未定位地址"}</strong><p>{phase === "preflight" ? "先確認欄位完整；這一步不會查地址、不會消耗地址查詢次數。" : phase === "geocoding" ? `正在進行地址比對，進度 ${geocodeProgress.done}/${geocodeProgress.total}；已完成的結果會立即保留。` : phase === "located" ? "所有配送地址都已取得定位，現在可以下載；下次重新匯入這份 CSV 會直接沿用。" : "部分地址沒有找到有效座標，可以修正原始 CSV 後重新匯入，再查詢未完成的地址。"}</p></div></section>
      {error ? <div className="qcl-message qcl-error"><Icon name="alert" size={17} />{error}</div> : null}
      {notice ? <div className="qcl-message qcl-success"><Icon name="check" size={17} />{notice}</div> : null}
      <section className="qcl-panel">
        <div className="qcl-panel-head"><div><div className="qcl-section-title">配送資料</div><h2>{fileName || "尚未選擇 CSV"}</h2><p>{rows.length ? `共 ${rows.length} 筆；請先確認紅色錯誤與黃色提醒。` : "請下載範本或選擇你準備好的配送 CSV。"}</p></div><div className="qcl-head-options"><label className="qcl-phone-option"><input type="checkbox" checked={autoAddMobileLeadingZero} onChange={handlePhoneFormatChange} /><span>手機號碼自動補 0</span><small>僅處理 9 碼手機號</small></label><label className="qcl-date"><Icon name="calendar" size={16} /><span>工作日期</span><input type="date" value={jobDate} onChange={(event) => setJobDate(event.target.value)} /></label></div></div>
        {rows.length ? <>
          <div className="qcl-summary"><div><strong>{rows.length}</strong><span>匯入筆數</span></div><div className="is-good"><strong>{validCount}</strong><span>資料可用</span></div><div className={errorCount ? "is-bad" : "is-good"}><strong>{errorCount}</strong><span>資料錯誤</span></div><div className={locatedCount ? "is-good" : "is-warn"}><strong>{locatedCount}</strong><span>已定位</span></div><div className={addressErrorCount ? "is-bad" : "is-good"}><strong>{addressErrorCount}</strong><span>地址問題</span></div><label className="qcl-search"><Icon name="search" size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜尋收件人、地址、定位狀態" /></label></div>
          <div className="qcl-table-wrap"><table className="qcl-table"><thead><tr><th>列</th><th>資料</th><th>地址狀態</th><th>收件人</th><th>聯絡電話</th><th>原始地址</th><th>定位後地址</th><th>貨物</th><th>重量／尺寸</th><th>材積／棧板</th><th>溫度</th><th>問題</th></tr></thead><tbody>{filteredRows.map((row) => <tr key={row.rowNumber} className={row.valid ? "is-valid" : "is-invalid"}><td>{row.rowNumber}</td><td><span className={`qcl-status ${row.valid ? "is-valid" : "is-invalid"}`}>{row.valid ? "可用" : "需修正"}</span></td><td><span className={`qcl-location-status is-${row.geocodeStatus}`}>{locationStatusLabel(row)}</span>{row.geocodeMessage ? <small className="qcl-location-message">{row.geocodeMessage}</small> : null}</td><td><strong>{row.recipientName || "未填"}</strong>{row.company ? <small>{row.company}</small> : null}</td><td>{row.phone || "未填"}</td><td className="qcl-address">{row.address || "未填"}{row.postalCode ? <small>{row.postalCode}</small> : null}</td><td className="qcl-address">{row.addressResolved || "—"}{row.resolvedPostalCode ? <small>{row.resolvedPostalCode}</small> : null}</td><td><strong>{cargoTypeLabel(row.cargoType)}</strong><small>{row.cargoName || "未填"} · {row.cargoCount} 件</small></td><td>{row.weightKg === null ? "未填" : `${row.weightKg.toLocaleString()} kg`}<small>{dimensionLabel(row)}</small></td><td>{row.volumeCbm === null ? "—" : `${row.volumeCbm} cbm`}<small>{row.palletCount === null ? "無棧板資料" : `${row.palletCount} 板`}</small></td><td><span className="qcl-chip">{temperatureLabel(row.temperatureType)}</span></td><td><div className="qcl-problems">{row.errors.map((item) => <span className="qcl-problem qcl-problem-error" key={`e-${item}`}>{item}</span>)}{row.warnings.map((item) => <span className="qcl-problem qcl-problem-warn" key={`w-${item}`}>{item}</span>)}{!row.errors.length && !row.warnings.length ? <span className="qcl-clean">資料完整</span> : null}</div></td></tr>)}</tbody></table>{!filteredRows.length ? <div className="qcl-empty-filter">找不到符合搜尋條件的資料。</div> : null}</div>
          <div className="qcl-panel-foot" aria-busy={geocoding}><span><Icon name={phase === "located" ? "check" : phase === "geocoding" ? "search" : "truck"} size={15} />{phase === "geocoding" ? `地址比對中 ${geocodeProgress.done}/${geocodeProgress.total}` : phase === "located" ? "定位結果已完成，可下載並留存這份 CSV。" : addressErrorCount ? `已有 ${locatedCount} 筆定位完成，請重新查詢未完成地址。` : "資料預檢完成後，才會進行地址比對。"}</span><div className="qcl-foot-actions">{phase === "geocoding" ? <button type="button" className="qcl-secondary" onClick={stopGeocoding}>停止查詢</button> : null}{phase === "located" ? <><button type="button" className="qcl-secondary" onClick={downloadLocated}><Icon name="download" size={15} />下載已定位 CSV</button><button type="button" className="qcl-primary qcl-next" onClick={continueToVehicleStep}><Icon name="truck" size={16} />進入選擇車輛</button></> : null}{phase !== "located" && phase !== "geocoding" ? <button type="button" className="qcl-primary qcl-next" onClick={startGeocoding} disabled={geocoding || Boolean(errorCount) || !rows.length}><Icon name={phase === "address_review" ? "search" : "check"} size={16} />{phase === "address_review" ? "重新查詢未完成地址" : "確認資料，開始地址比對"}</button> : null}</div></div>
        </> : <div className="qcl-empty"><div className="qcl-empty-icon"><Icon name="upload" size={25} /></div><h3>先匯入配送資料</h3><p>CSV 上傳後會在這裡逐列顯示錯誤、提醒與可進下一步的資料。</p><button type="button" className="qcl-secondary" onClick={() => downloadTextFile("LogisticsJobImportTemplate.csv", TEMPLATE_CSV)}><Icon name="download" size={15} />下載範本</button></div>}
      </section>
      {orgId ? <div className="qcl-org-note">已連結組織：{orgId}；此頁目前只做本機預檢。</div> : <div className="qcl-org-note">目前尚未取得 orgId；CSV 預檢仍可進行，但下一階段建立工作前需要組織資料。</div>}
      <style>{styles}</style>
    </div>
  );
}

const styles = `
.qcl-page{min-height:100%;box-sizing:border-box;padding:30px 34px 52px;background:#F5F6F8;color:#17212B;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Segoe UI",sans-serif;overflow-y:auto;-webkit-overflow-scrolling:touch}.qcl-header{max-width:1480px;margin:0 auto 20px;display:flex;align-items:flex-end;justify-content:space-between;gap:24px}.qcl-eyebrow{color:#9BA3AC;font-size:10px;font-weight:800;letter-spacing:1.8px}.qcl-header h1{margin:6px 0 5px;color:#1F2B35;font-size:29px;line-height:1.12;letter-spacing:-.6px}.qcl-header p{margin:0;color:#87919A;font-size:13px}.qcl-actions{display:flex;align-items:center;justify-content:flex-end;gap:9px;flex-wrap:wrap}.qcl-primary,.qcl-secondary{min-height:40px;padding:0 14px;display:inline-flex;align-items:center;justify-content:center;gap:7px;border-radius:9px;font:inherit;font-size:12px;font-weight:750;cursor:pointer;box-sizing:border-box}.qcl-primary{border:1px solid #F18432;background:#F18432;color:#FFF;box-shadow:0 6px 15px rgba(241,132,50,.16)}.qcl-primary:hover:not(:disabled){background:#DD6F20}.qcl-secondary{border:1px solid #DCE1E6;background:#FFF;color:#53606C}.qcl-secondary:hover{border-color:#F1A16A;color:#DD6F20;background:#FFFBF8}.qcl-primary input{display:none}.qcl-primary:disabled{cursor:not-allowed;opacity:.5}.qcl-info,.qcl-message,.qcl-panel,.qcl-org-note{max-width:1480px;margin-left:auto;margin-right:auto}.qcl-info{margin-bottom:12px;padding:13px 15px;display:flex;align-items:center;gap:11px;border:1px solid #F3D9BF;border-radius:11px;background:#FFF9F4}.qcl-info-icon{width:32px;height:32px;display:grid;place-items:center;border-radius:9px;background:#FFF0E5;color:#E4772B}.qcl-info strong{display:block;color:#7C542B;font-size:12px}.qcl-info p{margin:3px 0 0;color:#AC875E;font-size:10px}.qcl-message{margin-bottom:12px;padding:11px 14px;display:flex;align-items:center;gap:8px;border-radius:9px;font-size:12px}.qcl-error{border:1px solid #F0CACA;background:#FFF4F4;color:#B84444}.qcl-success{border:1px solid #CBE9D9;background:#F1FBF5;color:#218152}.qcl-panel{border:1px solid #E5E8EC;border-radius:16px;background:#FFF;overflow:hidden}.qcl-panel-head{padding:21px 22px 17px;display:flex;align-items:flex-start;justify-content:space-between;gap:20px;border-bottom:1px solid #EDF0F2}.qcl-section-title{color:#A0A8B0;font-size:10px;font-weight:800;letter-spacing:1px}.qcl-panel-head h2{margin:5px 0 4px;color:#33414C;font-size:18px}.qcl-panel-head p{margin:0;color:#98A1A9;font-size:11px}.qcl-head-options{display:flex;align-items:flex-end;justify-content:flex-end;gap:10px;flex-wrap:wrap}.qcl-phone-option{min-height:40px;padding:7px 10px;display:grid;grid-template-columns:auto 1fr;align-items:center;gap:2px 7px;border:1px solid #E1E6EA;border-radius:9px;background:#FCFDFD;color:#53606C;cursor:pointer}.qcl-phone-option input{width:14px;height:14px;grid-row:span 2;accent-color:#F18432}.qcl-phone-option span{font-size:10px;font-weight:700}.qcl-phone-option small{color:#9BA4AC;font-size:9px}.qcl-date{min-width:175px;padding:9px 10px;display:grid;grid-template-columns:auto 1fr;align-items:center;gap:3px 7px;border:1px solid #E1E6EA;border-radius:9px;color:#75818B}.qcl-date span{font-size:10px}.qcl-date input{grid-column:2;border:0;outline:0;color:#36434E;font:inherit;font-size:11px}.qcl-summary{padding:13px 16px;display:flex;align-items:center;gap:12px;border-bottom:1px solid #EDF0F2;background:#FCFDFD}.qcl-summary>div{min-width:72px;padding-right:13px;border-right:1px solid #E8ECEF;display:grid;gap:2px}.qcl-summary strong{color:#33414C;font-size:20px;line-height:1}.qcl-summary span{color:#98A1A9;font-size:9px}.qcl-summary .is-good strong{color:#218A57}.qcl-summary .is-bad strong{color:#C84D4D}.qcl-summary .is-warn strong{color:#B87927}.qcl-search{height:34px;min-width:220px;margin-left:auto;padding:0 9px;display:flex;align-items:center;gap:7px;border:1px solid #E1E6EA;border-radius:8px;color:#A0A9B1}.qcl-search:focus-within{border-color:#F1AC7D;box-shadow:0 0 0 3px rgba(241,132,50,.1)}.qcl-search input{width:100%;border:0;outline:0;background:transparent;color:#35424D;font:inherit;font-size:11px}.qcl-table-wrap{overflow:auto}.qcl-table{width:100%;min-width:1120px;border-collapse:collapse}.qcl-table th,.qcl-table td{padding:11px 12px;border-bottom:1px solid #F0F2F4;text-align:left;vertical-align:top;font-size:10px}.qcl-table th{background:#FAFBFC;color:#78848E;font-size:10px;font-weight:750;white-space:nowrap}.qcl-table td{color:#586570}.qcl-table td strong,.qcl-table td small{display:block}.qcl-table td strong{color:#3C4A55;font-size:11px}.qcl-table td small{margin-top:3px;color:#A1A9B0;font-size:9px}.qcl-table tr.is-invalid{background:#FFF9F9}.qcl-table tr:hover{background:#FFFCF9}.qcl-address{max-width:245px}.qcl-address small{color:#A77A4D}.qcl-status,.qcl-chip{display:inline-flex;align-items:center;padding:4px 7px;border-radius:6px;font-size:9px;font-weight:750;white-space:nowrap}.qcl-status.is-valid{background:#EAF8F0;color:#218A57}.qcl-status.is-invalid{background:#FFF0F0;color:#C84D4D}.qcl-chip{background:#F1F5FF;color:#5B77B6}.qcl-location-status{display:inline-flex;align-items:center;padding:4px 7px;border-radius:6px;background:#F4F5F7;color:#7B8790;font-size:9px;font-weight:750;white-space:nowrap}.qcl-location-status.is-located{background:#EAF8F0;color:#218A57}.qcl-location-status.is-searching{background:#FFF4DF;color:#B87927}.qcl-location-status.is-not_found,.qcl-location-status.is-error{background:#FFF0F0;color:#C84D4D}.qcl-location-message{max-width:160px;margin-top:4px;color:#9EA7AF;font-size:9px;line-height:1.35}.qcl-foot-actions{display:flex;align-items:center;justify-content:flex-end;gap:8px}.qcl-problems{display:grid;gap:4px;min-width:190px}.qcl-problem,.qcl-clean{display:block;line-height:1.35}.qcl-problem-error{color:#C84D4D}.qcl-problem-warn{color:#B87927}.qcl-clean{color:#218A57}.qcl-empty-filter{padding:32px;text-align:center;color:#9DA6AD;font-size:11px}.qcl-panel-foot{padding:14px 17px;display:flex;align-items:center;justify-content:space-between;gap:12px;border-top:1px solid #EDF0F2;background:#FCFDFD}.qcl-panel-foot>span{display:flex;align-items:center;gap:6px;color:#99A2AA;font-size:10px}.qcl-next{min-height:37px;box-shadow:none}.qcl-empty{min-height:370px;display:grid;place-items:center;align-content:center;gap:10px;color:#98A2AA;text-align:center}.qcl-empty-icon{width:58px;height:58px;display:grid;place-items:center;border-radius:17px;background:#FFF0E5;color:#E8792B}.qcl-empty h3{margin:2px 0 0;color:#4B5964;font-size:16px}.qcl-empty p{margin:0 0 5px;color:#A1A9B0;font-size:11px}.qcl-org-note{margin-top:10px;color:#A0A8B0;font-size:10px}.qcl-center{min-height:420px;display:grid;place-items:center;align-content:center;gap:10px;color:#7B8790;font-size:12px}.qcl-spinner{width:18px;height:18px;border:2px solid #F4D6BF;border-top-color:#F18432;border-radius:50%;animation:qclSpin .75s linear infinite}@keyframes qclSpin{to{transform:rotate(360deg)}}
@media(max-width:850px){.qcl-page{padding:23px 18px 40px}.qcl-header{display:block}.qcl-actions{margin-top:15px;justify-content:flex-start}.qcl-panel-head{display:block}.qcl-head-options{margin-top:14px;justify-content:flex-start}.qcl-date{max-width:230px}.qcl-summary{flex-wrap:wrap}.qcl-search{width:100%;min-width:0;order:5}.qcl-panel-foot{align-items:stretch;flex-direction:column}.qcl-panel-foot .qcl-next{width:100%}}
@media(max-width:520px){.qcl-page{padding:18px 12px 30px}.qcl-header h1{font-size:24px}.qcl-actions{display:grid;grid-template-columns:1fr}.qcl-actions>*{width:100%}.qcl-info{align-items:flex-start}.qcl-summary{gap:9px}.qcl-summary>div{min-width:62px;padding-right:8px}.qcl-panel-head{padding:17px 15px}.qcl-table th,.qcl-table td{padding:10px}.qcl-empty{min-height:300px}}
`;
